import * as React from 'react';
import { camelCase, isEmpty, isNil } from 'lodash';

import Tooltip from 'components/Tooltip';
import MoreButton from 'components/buttons/more-button/MoreButton';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import ClientApprovalEntryCaption from './components/client-approval-entry-caption/ClientApprovalEntryCaption';
import ClientApprovalEntryContent from './components/client-approval-entry-content/ClientApprovalEntryContent';
import ClientApprovalEntryNotes from './components/client-approval-entry-notes/ClientApprovalEntryNotes';

import { DELIVERABLE_ICONS } from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';

import type { ClientApprovalBucketV2, ClientApprovalStatus } from 'types';

import './client-approval-entry.scss';

applyTranslations(require('./client-approval-entry.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

type Props = {
  item: ClientApprovalBucketV2,
  onAddNote: (note: string) => void,
  onEntryDownload: () => void,
  onMessagesChecked: (ids: Array<number>) => void,
  onCaptionStatusChange: (status: ClientApprovalStatus) => void,
  onAssetApprovalChange: (itemId: number, status: ClientApprovalStatus) => void
};

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

const ClientApprovalEntry: React.FC<Props> = (
  {
    item,
    onAddNote,
    onEntryDownload,
    onMessagesChecked,
    onCaptionStatusChange,
    onAssetApprovalChange
  }: Props
) => {
  const taskName = __t(`collabary_common.types.${item.taskType}.singular`);
  const contextMenuItems = [
    {
      name: 'download-look',
      text: __t('client_approval_entry.context_menu.download_approved'),
      onClick: onEntryDownload,
      disabled: !item.items.find(entry => entry.status === 'approved')
    }
  ];

  return (
    <div className="client-approval-entry">
      <div className="client-approval-entry__header">
        <SVGDynamicIcon
          name={`${DELIVERABLE_ICONS[camelCase(item.taskType)]}`}
          height={21}
          width={21}
          customColor="black"
        />
        <Tooltip
          message={item.taskName || taskName}
          classNameWrapper="client-approval-entry__tooltip-wrapper"
          display="flex"
        >
          <div
            className="client-approval-entry__title text-overflow-ellipsis"
          >
            {item.taskName || taskName}
          </div>
        </Tooltip>
        <MoreButtonWithMenu
          width={20}
          contextMenuProps={{ items: contextMenuItems, zIndex: 100 }}
        />
      </div>
      <div className="client-approval-entry__main">
        {
          !isEmpty(item.items) && (
            <React.Fragment>
              {/* @ts-ignore prop missing is supplied through stateprops, still TS is complaining */}
              <ClientApprovalEntryContent
                item={item}
                onAssetApprovalChange={onAssetApprovalChange}
              />
              <VerticalSpacing type="xlarge" />
            </React.Fragment>
          )
        }
        {
          !isNil(item.caption) && (
            <ClientApprovalEntryCaption
              item={item}
              onCaptionStatusChange={onCaptionStatusChange}
            />
          )
        }
        <div className="client-approval-entry__separator" />
        <ClientApprovalEntryNotes
          isExpandable={!item.approvalNotes.length}
          item={item}
          onMessagesChecked={onMessagesChecked}
          onSendMessage={onAddNote}
        />
      </div>
    </div>
  );
};

export default ClientApprovalEntry;
